import { FC, useEffect, useRef } from 'react'
import { useAuth } from '../../hooks/auth'

interface AnalyticsProps {
    /** @deprecated Use application's projectName from auth state instead */
    projectName?: string
}

/**
 * Unified Analytics component
 *
 * Handles both page view tracking and user presence tracking in one component.
 * This component doesn't render anything visible but provides tracking functionality:
 * 1. Page view tracking on route changes
 * 2. User session tracking (start, heartbeat, end)
 * 3. User activity monitoring
 */
const Analytics: FC<AnalyticsProps> = ({ projectName }) => {
    const { user, application } = useAuth()

    // Determine which projectName to use, prioritizing application's projectName
    const effectiveProjectName = application?.projectName || projectName || user?.email || 'numeo-freight'

    // User presence tracking state
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const lastActivityRef = useRef<number>(Date.now())
    const isActiveRef = useRef<boolean>(false)

    // Configuration for presence tracking
    const HEARTBEAT_INTERVAL_MS = 60000 // Send heartbeat every minute
    const USER_ACTIVITY_TIMEOUT_MS = 600000 // Consider user inactive after 10 minutes without activity

    // Start tracking session
    const startSession = () => {
        if (!isActiveRef.current) {
            isActiveRef.current = true
            lastActivityRef.current = Date.now()

            // Set up heartbeat interval
            if (heartbeatIntervalRef.current === null) {
                heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
            }
        }
    }

    // End tracking session
    const endSession = () => {
        if (isActiveRef.current) {
            isActiveRef.current = false

            // Clear heartbeat interval
            if (heartbeatIntervalRef.current !== null) {
                clearInterval(heartbeatIntervalRef.current)
                heartbeatIntervalRef.current = null
            }
        }
    }

    // Send heartbeat event
    const sendHeartbeat = () => {
        // Check if user is still active
        const inactiveTimeMs = Date.now() - lastActivityRef.current

        if (inactiveTimeMs > USER_ACTIVITY_TIMEOUT_MS) {
            // User has been inactive for too long, end the session
            endSession()
            return
        }
    }

    // Record user activity
    const recordActivity = () => {
        const now = Date.now()
        lastActivityRef.current = now

        // If not already tracking, start a new session
        if (!isActiveRef.current) {
            startSession()
        }
    }

    // User presence tracking
    useEffect(() => {
        // Start session when component mounts
        startSession()

        // Set up event listeners for user activity
        const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll']
        activityEvents.forEach((eventType) => {
            window.addEventListener(eventType, recordActivity)
        })

        // Set up visibility change detection
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                startSession()
            } else {
                endSession()
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)

        // Set up beforeunload to track when user leaves
        const handleBeforeUnload = () => {
            endSession()
        }
        window.addEventListener('beforeunload', handleBeforeUnload)

        // Cleanup
        return () => {
            // Remove all event listeners
            activityEvents.forEach((eventType) => {
                window.removeEventListener(eventType, recordActivity)
            })
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('beforeunload', handleBeforeUnload)

            // Clear interval and end session
            if (heartbeatIntervalRef.current !== null) {
                clearInterval(heartbeatIntervalRef.current)
            }
            endSession()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, effectiveProjectName]) // Include all dependencies

    // This component doesn't render anything
    return null
}

export default Analytics
